import { parseDelimited } from './scope.js';
import type { PrincipalType, Role, Store, UserRole } from './types.js';
import { ALL_ROLES } from './types.js';

/**
 * SharePoint row → app type mappings shared by the browser data layer AND
 * the server API (api/), so both read UserRoles and Stores the same way —
 * the server's role resolution must match what the app shows. Pure: no
 * Graph or browser imports (the server compiles this file under NodeNext,
 * hence the explicit .js import paths).
 */

/** SharePoint field shape for the UserRoles list (Title = display name). */
export interface UserRoleFields {
  Title: string;
  PrincipalType: string;
  UPN: string;
  GroupId: string;
  Role: string;
  Districts: string;
  StoreNumbers: string;
  Active: boolean;
}

export function toUserRole(id: string, f: Partial<UserRoleFields>): UserRole {
  const role = ALL_ROLES.includes(f.Role as Role)
    ? (f.Role as Role)
    : 'StoreTeam';
  const principalType: PrincipalType =
    f.PrincipalType === 'group' ? 'group' : 'user';
  return {
    id,
    principalType,
    displayName: f.Title ?? '',
    upn: (f.UPN ?? '').trim(),
    groupId: f.GroupId ?? '',
    role,
    districts: parseDelimited(f.Districts),
    storeNumbers: parseDelimited(f.StoreNumbers),
    // Authorization flag: fail closed when the field is missing/null.
    active: f.Active ?? false,
  };
}

/** SharePoint field shape for the Stores list (Title = store name). */
export interface StoreFields {
  Title: string;
  StoreNumber: string;
  District: string;
  Region: string;
  StateCode: string;
  Address: string;
  City: string;
  Zip: string;
  StorePhone: string;
  /** Owners list item id (2026-10-28). The old free-text `Owner` column is deprecated — never read or written. */
  OwnerId: number | null;
  HubSite: string;
  ManagerEmail: string;
  DmName: string;
  DmUpn: string;
  Active: boolean;
  /** Choice Yes/No; blank = unknown. */
  ParkingOnSite: string;
  /** Blank = unknown; 0 = none; N = count. */
  ElevatorCount: number | null;
  /** Yes = long-distance store; blank/No = not (a scheduling setting, not authz). */
  LongDistance: boolean | null;
}

export function toStore(id: string, f: Partial<StoreFields>): Store {
  return {
    id,
    name: f.Title ?? '',
    storeNumber: f.StoreNumber ?? '',
    district: f.District ?? '',
    region: f.Region ?? '',
    state: f.StateCode ?? '',
    address: f.Address ?? '',
    city: f.City ?? '',
    zip: f.Zip ?? '',
    phone: f.StorePhone ?? '',
    ownerId: typeof f.OwnerId === 'number' && Number.isInteger(f.OwnerId) && f.OwnerId > 0 ? f.OwnerId : null,
    hubSite: f.HubSite ?? '',
    managerEmail: (f.ManagerEmail ?? '').trim(),
    dmName: f.DmName ?? '',
    dmUpn: (f.DmUpn ?? '').trim(),
    active: f.Active ?? false,
    parking:
      f.ParkingOnSite?.toLowerCase() === 'yes'
        ? 'yes'
        : f.ParkingOnSite?.toLowerCase() === 'no'
          ? 'no'
          : 'unknown',
    elevatorCount:
      typeof f.ElevatorCount === 'number' && f.ElevatorCount >= 0
        ? f.ElevatorCount
        : null,
    longDistance: f.LongDistance === true,
  };
}

